import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { rpc } from '@/lib/rpc';
import { supabase } from '@/lib/supabase';
import { leadKeys } from '@/features/leads/useLeads';
import type { UnitType } from '@/types/app';

export const inventoryKeys = {
  all: ['inventory'] as const,
  developers: () => ['inventory', 'developers'] as const,
  projects: (developerId?: string) => ['inventory', 'projects', developerId ?? 'all'] as const,
  units: (filters: UnitFilters) => ['inventory', 'units', filters] as const,
  matches: (leadId: string, revision?: string) =>
    ['inventory', 'matches', leadId, revision ?? ''] as const,
};

export interface Developer {
  id: string;
  name: string;
  areas: string[];
  commission_percent: string | null;
  contact_name: string | null;
  contact_phone: string | null;
  status: string;
}

export interface Project {
  id: string;
  developer_id: string;
  name: string;
  area: string | null;
  delivery_date: string | null;
  maintenance_percent: string | null;
  status: string;
}

export interface Unit {
  id: string;
  project_id: string;
  developer_id: string;
  unit_code: string;
  type: UnitType;
  area_sqm: string | null;
  bedrooms: number | null;
  bathrooms: number | null;
  floor: number | null;
  price_total: string;
  down_payment: string | null;
  delivery_date: string | null;
  availability: 'available' | 'reserved' | 'sold' | 'blocked';
  reserved_until: string | null;
  projects: { name: string; area: string | null } | null;
}

// prettier-ignore
const DEVELOPER_COLUMNS = 'id, name, areas, commission_percent, contact_name, contact_phone, status';
// prettier-ignore
const PROJECT_COLUMNS = 'id, developer_id, name, area, delivery_date, maintenance_percent, status';
// prettier-ignore
const UNIT_COLUMNS = 'id, project_id, developer_id, unit_code, type, area_sqm, bedrooms, bathrooms, floor, price_total, down_payment, delivery_date, availability, reserved_until, projects(name, area)';

export function useDevelopers() {
  return useQuery({
    queryKey: inventoryKeys.developers(),
    queryFn: async (): Promise<Developer[]> => {
      const { data, error } = await supabase
        .from('developers')
        .select(DEVELOPER_COLUMNS)
        .order('name');
      if (error) throw error;
      return data ?? [];
    },
  });
}

export function useProjects(developerId?: string) {
  return useQuery({
    queryKey: inventoryKeys.projects(developerId),
    queryFn: async (): Promise<Project[]> => {
      let query = supabase.from('projects').select(PROJECT_COLUMNS).order('name');
      if (developerId) query = query.eq('developer_id', developerId);
      const { data, error } = await query;
      if (error) throw error;
      return data ?? [];
    },
  });
}

export interface UnitFilters {
  projectId?: string;
  availability?: string;
  type?: string;
  minPrice?: string;
  maxPrice?: string;
  bedrooms?: string;
}

export function useUnits(filters: UnitFilters) {
  return useQuery({
    queryKey: inventoryKeys.units(filters),
    queryFn: async (): Promise<Unit[]> => {
      let query = supabase.from('units').select(UNIT_COLUMNS);

      if (filters.projectId) query = query.eq('project_id', filters.projectId);
      if (filters.availability) query = query.eq('availability', filters.availability);
      if (filters.type) query = query.eq('type', filters.type);
      if (filters.bedrooms) query = query.eq('bedrooms', Number(filters.bedrooms));
      if (filters.minPrice) query = query.gte('price_total', Number(filters.minPrice));
      if (filters.maxPrice) query = query.lte('price_total', Number(filters.maxPrice));

      const { data, error } = await query.order('price_total').limit(200);
      if (error) throw error;
      return (data ?? []) as unknown as Unit[];
    },
  });
}

export interface MatchRow {
  unit_id: string;
  project_id: string;
  project_name: string;
  developer_name: string;
  unit_code: string;
  unit_type: UnitType;
  area_sqm: string | null;
  bedrooms: number | null;
  price_total: string;
  down_payment: string | null;
  delivery_date: string | null;
  score: number;
  breakdown: Record<string, { score: number; max: number }>;
}

/**
 * Matching units for a lead (#22).
 *
 * `revision` is the lead's `updated_at`, and it is part of the cache key on purpose.
 * "Changing a lead's budget changes its matches immediately" is an acceptance
 * criterion, and staleTime alone does not deliver it: editing the budget invalidates
 * the lead queries, not this one, so without the revision in the key a rep adjusting
 * the budget on the phone would keep seeing the previous ranking until the panel
 * happened to remount.
 *
 * Keying on the lead's own revision rather than invalidating from useUpdateLead
 * keeps the dependency pointing one way — this module already reads leadKeys, and
 * the reverse would make a cycle.
 */
export function useMatches(leadId: string | undefined, revision?: string) {
  return useQuery({
    queryKey: leadId ? inventoryKeys.matches(leadId, revision) : ['inventory', 'matches', 'none'],
    enabled: Boolean(leadId),
    staleTime: 0,
    queryFn: () => rpc<MatchRow[]>('match_units', { p_lead_id: leadId!, p_limit: 12 }),
  });
}

export function useReserveUnit() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      unitId,
      leadId,
      holdDays,
    }: {
      unitId: string;
      leadId: string;
      holdDays?: number;
    }) =>
      rpc<Unit>('reserve_unit', {
        p_unit_id: unitId,
        p_lead_id: leadId,
        p_hold_days: holdDays ?? 7,
      }),
    onSuccess: (_d, { leadId }) => {
      void queryClient.invalidateQueries({ queryKey: inventoryKeys.all });
      void queryClient.invalidateQueries({ queryKey: leadKeys.detail(leadId) });
    },
  });
}

/**
 * Release a hold (#21). Supervisory only — release_unit() rejects a rep outright,
 * so the button is hidden for them rather than failing on click.
 */
export function useReleaseUnit() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ unitId, reason }: { unitId: string; reason?: string }) =>
      rpc<Unit>('release_unit', { p_unit_id: unitId, p_reason: reason ?? null }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: inventoryKeys.all }),
  });
}

export function useUpsertDeveloper() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      name: string;
      id?: string | null;
      areas?: string[];
      commissionPercent?: number | null;
      contactName?: string | null;
      contactPhone?: string | null;
    }) =>
      rpc<Developer>('upsert_developer', {
        p_name: input.name,
        p_id: input.id ?? null,
        p_areas: input.areas ?? [],
        p_commission_percent: input.commissionPercent ?? null,
        p_contact_name: input.contactName ?? null,
        p_contact_phone: input.contactPhone ?? null,
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: inventoryKeys.all }),
  });
}

export function useUpsertProject() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      developerId: string;
      name: string;
      id?: string | null;
      area?: string | null;
      deliveryDate?: string | null;
    }) =>
      rpc<Project>('upsert_project', {
        p_developer_id: input.developerId,
        p_name: input.name,
        p_id: input.id ?? null,
        p_area: input.area ?? null,
        p_delivery_date: input.deliveryDate ?? null,
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: inventoryKeys.all }),
  });
}
